// Small optional quests that refresh every 3 days (not daily, so rest days stay rest days). Each one fits
// in a single session. The quest of a period is picked from the period number, so it is the same on every
// device, and never the same quest twice in a row.
import { addDays, dayNumber, daysBetween, fromDayNumber, isoWeekday, localDay, type Day } from './dates';
import { hashUnit } from './ids';
import { counts, type HabitPlanEntry, type HabitSession } from './model';
import { exercisesDone } from './xp';

export const QUEST_PERIOD_DAYS = 3;
/** Periods are counted from this Monday. */
const ANCHOR: Day = '2026-01-05';

export interface QuestPeriod {
  index: number;
  start: Day;
  end: Day;
}

export function questPeriod(day: Day): QuestPeriod {
  const index = Math.floor(daysBetween(ANCHOR, day) / QUEST_PERIOD_DAYS);
  const start = fromDayNumber(dayNumber(ANCHOR) + index * QUEST_PERIOD_DAYS);
  return { index, start, end: addDays(start, QUEST_PERIOD_DAYS - 1) };
}

interface QuestContext {
  /** Sessions in the period with the main part done (ended-early sessions never finish a quest), oldest first. */
  inPeriod: HabitSession[];
  /** Sessions before the period. */
  before: HabitSession[];
  plannedWeekdays: Set<number>;
  /** The plan calendar: whether a day had a planned session (wins over the weekdays). */
  isPlanned?: (day: Day) => boolean;
  timeZone?: string;
}

export interface QuestDef {
  id: string;
  title: string;
  detail: string;
  target: number;
  progress: (c: QuestContext) => number;
}

const RECENT_DAYS = 30;

export const QUESTS: QuestDef[] = [
  {
    id: 'new_exercise', title: 'Try one new exercise', detail: 'Do an exercise you have never done in a session.', target: 1,
    progress: (c) => {
      const seen = new Set(c.before.flatMap((s) => [...exercisesDone(s)]));
      return c.before.length === 0 ? 0 : new Set(c.inPeriod.flatMap((s) => [...exercisesDone(s)].filter((id) => !seen.has(id)))).size;
    },
  },
  {
    id: 'whole_session', title: 'Warm-up to cool-down', detail: 'Finish a whole session, warm-up and cool-down included.', target: 1,
    progress: (c) => c.inPeriod.filter((s) => s.completed).length,
  },
  {
    id: 'planned_day', title: 'Keep a date with your plan', detail: 'Train on one of your planned days.', target: 1,
    progress: (c) => c.inPeriod.filter((s) => {
      const day = localDay(s.startedAt, c.timeZone);
      return c.isPlanned ? c.isPlanned(day) : c.plannedWeekdays.size === 0 || c.plannedWeekdays.has(isoWeekday(day));
    }).length,
  },
  {
    id: 'reps_150', title: '150 reps', detail: 'Do 150 reps in total in these 3 days.', target: 150,
    progress: (c) => c.inPeriod.reduce((sum, s) => sum + (s.blocks ?? []).reduce((n, b) => n + (b.skipped ? 0 : b.done.reps), 0), 0),
  },
  {
    id: 'minutes_15', title: '15 active minutes', detail: 'Train 15 minutes in total in these 3 days.', target: 15,
    progress: (c) => Math.floor(c.inPeriod.reduce((sum, s) => sum + Math.max(0, s.activeSec), 0) / 60),
  },
  {
    id: 'fresh_workout', title: 'Something different', detail: `Do a workout you have not done in the last ${RECENT_DAYS} days.`, target: 1,
    progress: (c) => c.inPeriod.filter((s) => {
      return !c.before.some((b) => b.workoutId === s.workoutId && s.startedAt - b.startedAt < RECENT_DAYS * 86_400_000)
        && !c.inPeriod.some((b) => b !== s && b.workoutId === s.workoutId && b.startedAt < s.startedAt);
    }).length,
  },
];

/** A shuffled order of all quests for one cycle (one cycle has as many periods as there are quests). */
function cycleOrder(cycle: number): number[] {
  return QUESTS.map((_, i) => i).sort((a, b) => hashUnit(`quest|${cycle}|${a}`) - hashUnit(`quest|${cycle}|${b}`) || a - b);
}

/**
 * The quest of the period. Each cycle goes through every quest once in a shuffled order, so a quest never
 * repeats inside a cycle; where two cycles meet, the first two of the new cycle swap places if needed.
 * The pick depends only on the period, so it is the same on every device.
 */
export function questFor(period: QuestPeriod): QuestDef {
  const n = QUESTS.length;
  const cycle = Math.floor(period.index / n);
  const order = cycleOrder(cycle);
  const previousLast = cycleOrder(cycle - 1)[n - 1];
  if (order[0] === previousLast) [order[0], order[1]] = [order[1]!, order[0]!];
  return QUESTS[order[period.index - cycle * n]!]!;
}

export interface QuestStatus {
  quest: QuestDef;
  period: QuestPeriod;
  progress: number;
  done: boolean;
  /** Days left including today. */
  daysLeft: number;
}

export function questStatus(input: { today: Day; sessions: HabitSession[]; plan: Pick<HabitPlanEntry, 'weekday'>[]; isPlanned?: (day: Day) => boolean; timeZone?: string }): QuestStatus {
  const period = questPeriod(input.today);
  const quest = questFor(period);
  const sorted = [...input.sessions].sort((a, b) => a.startedAt - b.startedAt);
  const dayOf = (s: HabitSession) => localDay(s.startedAt, input.timeZone);
  const ctx: QuestContext = {
    inPeriod: sorted.filter((s) => counts(s) && dayOf(s) >= period.start && dayOf(s) <= period.end),
    before: sorted.filter((s) => dayOf(s) < period.start),
    plannedWeekdays: new Set(input.plan.map((p) => p.weekday)),
    isPlanned: input.isPlanned,
    timeZone: input.timeZone,
  };
  const progress = Math.min(quest.target, quest.progress(ctx));
  return { quest, period, progress, done: progress >= quest.target, daysLeft: daysBetween(input.today, period.end) + 1 };
}
